import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Caption } from './ExportPreview';
import { FX, useClipClock, wordTimes } from './captionClock';
import { CAPTION_PRESETS } from './captionPresets';
import { EXPORT_TEXT } from './exportText';

// The export window's caption style picker: every preset as a little frame that PLAYS the clip
// on show in that style (its poster + its words at their real times), like the mockup page.
// One click sets the preset; the font, colours and size can still be changed after, and the
// selected tile follows them. One clock drives every tile.

const CHERRY = '#EC2B57';
const MUTED = '#a79fae';

const Tile = ({ sample, look, vertical, blur, width, time, selected, label, desc = '', onClick, testId }) => (
  <Box
    component="button"
    type="button"
    aria-pressed={selected}
    data-testid={testId}
    title={desc || label}
    onClick={onClick}
    sx={{
      flexShrink: 0,
      width,
      p: 0,
      m: 0,
      border: 'none',
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      textAlign: 'left',
      '&:focus-visible > div:first-of-type': { outline: '2px solid #ff6b8b', outlineOffset: '2px' },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: vertical ? '9 / 16' : '16 / 9',
        borderRadius: '10px',
        overflow: 'hidden',
        background: sample.thumbnail ? '#000' : 'linear-gradient(160deg, #2a2230, #0f0d12)',
        boxShadow: selected ? `0 0 0 2px ${CHERRY}` : '0 0 0 1px rgba(255,255,255,0.12)',
        containerType: 'inline-size',
      }}
    >
      {sample.thumbnail && blur ? (
        // Vertical · Blur: the whole picture over a blurred copy, like the big preview
        <>
          <img src={sample.thumbnail} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(6px)', transform: 'scale(1.2)' }} />
          <img src={sample.thumbnail} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
        </>
      ) : sample.thumbnail ? (
        <img src={sample.thumbnail} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : null}
      <Caption
        text={sample.text}
        nouns={sample.nouns || ''}
        wordStarts={sample.wordStarts || null}
        seconds={sample.seconds || 0}
        vertical={vertical}
        time={time}
        testId="preset-tile-caption"
        {...look}
      />
    </Box>
    <Box sx={{ fontSize: 12, fontWeight: selected ? 700 : 600, color: selected ? '#fff' : '#d9d2de', lineHeight: 1.25 }}>{label}</Box>
    {desc ? <Box sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>{desc}</Box> : null}
  </Box>
);

/**
 * presetKey: the chosen preset ('' = your own mix). onPick(key). sample: the clip on show
 * ({thumbnail, text, nouns, wordStarts, seconds}). lookFor(key): the Caption props a tile draws
 * with — a preset's own look, or (selected / your own) what the window has now. vertical: the
 * export is 9:16 (blur: Vertical · Blur, the picture whole over a blurred fill).
 */
const PresetPicker = ({ presetKey, onPick, sample, lookFor, vertical, blur = false }) => {
  const [open, setOpen] = useState(false);
  const words = useMemo(() => (sample.text || '').split(/\s+/).filter(Boolean), [sample.text]);
  const times = useMemo(() => wordTimes(words.length, sample.wordStarts, sample.seconds), [words.length, sample.wordStarts, sample.seconds]);
  const span = Math.max(sample.seconds || 0, (words.length ? times[words.length - 1] : 0) + 0.4);
  const t = useClipClock(words.length > 0, span + FX.hold);
  const time = Math.min(t, span);
  const stripW = vertical ? 84 : 132;
  const bigW = vertical ? 150 : 236;
  const tiles = [{ key: '', label: EXPORT_TEXT.OWN_STYLE, desc: EXPORT_TEXT.OWN_STYLE_HINT }, ...CAPTION_PRESETS];
  const pick = (key) => {
    onPick(key);
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} role="group" aria-label={EXPORT_TEXT.CAPTION_STYLE}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
        <Box component="span" sx={{ fontSize: 12, color: MUTED }}>{EXPORT_TEXT.CAPTION_STYLE}</Box>
        <Box component="span" sx={{ fontSize: 11, color: '#756d7c', flex: 1 }}>{EXPORT_TEXT.CAPTION_STYLE_HINT}</Box>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{ font: 'inherit', fontSize: 12, fontWeight: 600, color: '#ff6b8b', background: 'none', border: 'none', cursor: 'pointer', p: 0, whiteSpace: 'nowrap', '&:focus-visible': { outline: '2px solid #ff6b8b', outlineOffset: '2px' } }}
        >
          {EXPORT_TEXT.ALL_STYLES}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'thin' }}>
        {tiles.map((p) => (
          <Tile
            key={p.key || 'own'}
            testId={`preset-${p.key || 'own'}`}
            sample={sample}
            look={lookFor(p.key)}
            vertical={vertical}
            blur={blur}
            width={stripW}
            time={time}
            selected={presetKey === p.key}
            label={p.label}
            onClick={() => onPick(p.key)}
          />
        ))}
      </Box>

      <Modal open={open} onClose={() => setOpen(false)} BackdropProps={{ sx: { backgroundColor: 'rgba(8,6,10,0.6)' } }}>
        <Box
          role="dialog"
          aria-label={EXPORT_TEXT.STYLES_TITLE}
          sx={{
            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
            width: 'min(980px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
            background: '#16121a', color: '#f4f1f6', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6)', p: { xs: 2, md: 3 }, boxSizing: 'border-box', outline: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontSize: 20, fontWeight: 700 }}>{EXPORT_TEXT.STYLES_TITLE}</Box>
              <Box sx={{ fontSize: 13, color: MUTED }}>{EXPORT_TEXT.STYLES_SUB}</Box>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setOpen(false)}
              sx={{ font: 'inherit', fontSize: 13, fontWeight: 600, height: 34, px: 2, borderRadius: '9px', cursor: 'pointer', background: 'transparent', color: '#d9d2de', border: '1px solid rgba(255,255,255,0.14)' }}
            >
              {EXPORT_TEXT.CLOSE}
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${bigW}px, 1fr))`, gap: 2.5 }}>
            {tiles.map((p) => (
              <Tile
                key={p.key || 'own'}
                testId={`preset-all-${p.key || 'own'}`}
                sample={sample}
                look={lookFor(p.key)}
                vertical={vertical}
                blur={blur}
                width="100%"
                time={time}
                selected={presetKey === p.key}
                label={p.label}
                desc={p.desc}
                onClick={() => pick(p.key)}
              />
            ))}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PresetPicker;
